
// import { useGetShippersSelectQuery } from "../../../../../newapi/shipper/shipperSlice";
import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, IconButton, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, useEffect, useState } from 'react';
import { ReactComponent as BolLogo } from '../../styles/svg/bol.svg';
import { ReactComponent as DhlLogo } from '../../styles/svg/dhl.svg';
import { ReactComponent as DpdLogo } from '../../styles/svg/dpd.svg';
import { ReactComponent as PostNLLogo } from '../../styles/svg/postnl.svg';
import { DropdownBase, DropdownBaseV2, ShDropdownSearchQuery } from '../theme/dropdowns/Dropdown';
import Text from '../theme/text/Text';
import { arrayMove, useSortable } from '@dnd-kit/sortable';
import SouthOutlinedIcon from '@mui/icons-material/SouthOutlined';
import NorthOutlinedIcon from '@mui/icons-material/NorthOutlined';
import { CSS } from '@dnd-kit/utilities';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import TextInput from '../theme/inputs/TextInput';
import { getMobileCondition, getConditionKey, textValueTypes, numberValueTypes, dropdownValueTypes, getListOptions, searchableListValueTypes, booleanValueTypes } from '../Streams/Conditions/ConditionSettings';
import { CRValueMultiSelect } from '../Streams/Conditions/CRValueMultiSelect';
import { CRValueSingleSelect } from '../Streams/Conditions/CRValueSingleSelect';
import { useGetConditionValuesQuery } from '../../newapi/global/stream/streamSlice';

import SaveIcon from '@mui/icons-material/Save';
import {useGetShippersSelectWithProfilesQuery} from '../../newapi/shipper/shipperSlice';
import { useGetProductsSelectQuery } from '../../newapi/warehouse/productSlice';
import { useUpdateIndividualCostRuleMutation } from '../../newapi/financial/costSlice';
import { showToastMessageRequestError, showToastMessageUpdateRequest } from '../../utils/toasts';

import MConfirmDelete from '../global/Modals/MConfirmDelete';
import { condition_object_type } from '../../utils/staticEnums';
import ShSwitch from '../theme/inputs/ShSwitch';

const CustomComponent = (props) => {
    return (
      <Box>
        {props.index}&nbsp;
        {props.isSelected ? "yes " : "no "}
        {props.option}
      </Box>
    );
};

const ShipperLogoMap = {
    postnl: PostNLLogo,
    dhl: DhlLogo,
    dpd: DpdLogo,
    bol: BolLogo,
};

// Static cost-rule palette (theme-independent), mirrors the in-component colorSettings.
const COST_COLORS = {
    base: "#31415f",
    background: "#ecf0f5",
    costrule: "#ced7e1",
    costrulehover: "#8E96A7",
};

// Condition-row elements as styled components: their CSS classes are generated once
// when this module evaluates, instead of MUI re-running styleFunctionSx + emotion
// serialization on every `sx` Box on every render. This is the hot path when a row
// renders ~9 conditions, each with several styled boxes.
const ConditionRow = styled(Box)(({ theme }) => {
    const colors = tokens(theme.palette.mode);
    return {
        height: 40,
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0.5),
        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
        borderRadius: theme.shape.borderRadius * 5,
    };
});

const KeyTrigger = styled(Box)(({ theme }) => ({
    width: 120,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    height: 32,
    borderRadius: theme.shape.borderRadius * 4,
    "&:hover": {
        background: COST_COLORS.costrulehover,
        cursor: "pointer",
    },
    "@media screen and (max-width: 768px)": {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}));

const NominatorTrigger = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 155,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    borderRadius: theme.shape.borderRadius * 4,
    "&:hover": {
        background: COST_COLORS.costrulehover,
        cursor: "pointer",
    },
    "@media screen and (max-width: 768px)": {
        width: 32,
        padding: 0,
        marginLeft: 0,
        marginRight: 0,
    },
}));

const ValueInputBox = styled(Box)(({ theme }) => {
    const colors = tokens(theme.palette.mode);
    return {
        display: "inline-flex",
        marginLeft: "auto",
        background: colors.grey[100],
        height: 32,
        borderRadius: theme.shape.borderRadius * 4,
        width: 120,
        "&:hover": {
            background: colors.grey[200],
            cursor: "pointer",
        },
        "&.error": {
            background: colors.red[100],
        },
    };
});

const ValueSwitchBox = styled(Box)({
    display: "inline-flex",
    marginLeft: "auto",
});

const SearchTrigger = styled(ButtonBase)(({ theme }) => {
    const colors = tokens(theme.palette.mode);
    return {
        marginLeft: "auto",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: colors.grey[100],
        height: 32,
        minWidth: 120,
        borderRadius: theme.shape.borderRadius * 4,
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
        "&:hover": {
            background: colors.grey[200],
            cursor: "pointer",
        },
    };
});

const RemoveButton = styled(Box)(({ theme }) => {
    const colors = tokens(theme.palette.mode);
    return {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 32,
        width: 32,
        borderRadius: theme.shape.borderRadius * 4,
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(0.5),
        color: colors.txt["secondary"],
        "&:hover": {
            background: COST_COLORS.costrulehover,
            cursor: "pointer",
        },
    };
});

const CostRule = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    const [updateCostRule, {isLoading:isUpdatingCostRule}] = useUpdateIndividualCostRuleMutation();
    const [updateTrigger, setUpdateTrigger] = useState(0);

    const {attributes,listeners,setNodeRef,transform,transition} = useSortable({id: props.costRule.id, disabled: props.disableDnD})

    // Move this rule up or down by one step. Order is persisted on save (priority = index).
    const moveCostRule = (fromIndex, toIndex) => {
        props.setCostRules(prev => {
            if (toIndex < 0 || toIndex >= prev.length) return prev;
            return arrayMove(prev, fromIndex, toIndex);
        });
    };
    
    const style={
        transition,
        transform:CSS.Transform.toString(transform)
    }
    

    const [windowSize, setWindowSize] = useState(window.innerWidth);
    const handleResize = (event) => {
        setWindowSize(window.innerWidth);
    }

    useEffect(() => {
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        }
    }, []);

    const nominatorListValues = ["isinselection", "isnotinselection"];

    const { data: conditionValues, isLoading: conditionValuesLoading } = useGetConditionValuesQuery(props.conditionType ?? "cost");
    const [conditionValueSelect, setConditionValueSelect] = useState([]);

    useEffect(() => {
        if(conditionValues) {
            setConditionValueSelect(
                conditionValues.conditions.map(value => {
                    const name = value.objectType && [condition_object_type.ORDERFIELD, condition_object_type.PRODUCTFIELD].includes(value.objectType) ? value.key  // Dynamic field                    // no translation prefix for dynamic
                        : t(`condition_${value.key}`); // Static field

                    let objectType = null;
                    if (value.objectType === condition_object_type.ORDERFIELD) {
                        objectType = { objectType: condition_object_type.ORDERFIELD };
                    } else if (value.objectType === condition_object_type.PRODUCTFIELD) {
                        objectType = { objectType: condition_object_type.PRODUCTFIELD };
                    }

                    return {
                        name: name,
                        key: value.key,
                        ...(objectType ? objectType : {}) 
                    }
                    
                })
            );

            if (props.costRule.conditions) {
                setConditionsFromLoad();
            }
        }
    }, [conditionValues]);

     const getObjects = (ruleline,t) => {
         return getListOptions(ruleline?.valueType, props, t, true, ruleline?.listValues);
    }

    const setConditionsFromLoad = () => {
        const conditionsCopy = props.costRule.conditions.map(condition => {
            // Get nominator options, value type, and listValues for the new key
            const conditionFollowUpOptions = getConditionFollowUpOptions(condition.key);

            // Create a new object with all properties
            return {
                ...condition,
                nominatorOptions: conditionFollowUpOptions.nominatorOptions,
                valueType: conditionFollowUpOptions.valueType,
                listValues: conditionFollowUpOptions.listValues
            };
        });

        props.setCostRules(currentCostRules => {
            const ruleIndex = currentCostRules.findIndex(rule => rule.priority === props.costRule.priority);
            if (ruleIndex > -1) {
                let updatedCostRules = [...currentCostRules];
                // Update the rule with the new conditions
                updatedCostRules[ruleIndex] = { ...updatedCostRules[ruleIndex], conditions: conditionsCopy };

                return updatedCostRules;
            }
        });
    }

    const getConditionFollowUpOptions = (key) => {
        const condition = conditionValues?.conditions?.find(value => value.key === key);
        return {
            nominatorOptions: condition?.values ?? [],
            valueType: condition?.valueType ?? "string",
            listValues: condition?.listValues ?? []
        }
    }
    
    const colorSettings = {
        base: "#31415f",
        background: "#ecf0f5",
        costrule: "#ced7e1",
        costrulehover: "#8E96A7",
    };

        const saveRule= async (rule) => {
            try {
                if (isUpdatingCostRule) return;
                const data = await updateCostRule({id: props.costId,rule}).unwrap();
                showToastMessageUpdateRequest(t,data);
                setUpdateTrigger(prev => prev + 1);
            } catch (err) {
                showToastMessageRequestError(t("create_failed"), t, err);
            }
        }

    // useEffect(() => {
    //     console.log(props.costRule)
    // }, [props.costRule]);

    const updateCostRuleLine = (lineIndex, newRuleLine) => {
        props.setCostRules(currentCostRules => {
            // Find the index of the rule that needs to be updated
            const ruleIndex = currentCostRules.findIndex(rule => rule.priority === props.costRule.priority);
    
            // If the rule is found, proceed to update
            if (ruleIndex > -1) {
                // Clone the stream rules array
                let updatedCostRules = [...currentCostRules];
                // Clone the conditions array from the found rule
                let updatedCostRuleLines = [...updatedCostRules[ruleIndex].conditions];
                // Replace the specific rule line with the new rule line
                updatedCostRuleLines[lineIndex] = { ...updatedCostRuleLines[lineIndex], ...newRuleLine };

                // Update the rule with the new conditions
                updatedCostRules[ruleIndex] = { ...updatedCostRules[ruleIndex], conditions: updatedCostRuleLines };
    
                return updatedCostRules;
            }
    
            // If the rule wasn't found, return the current state unchanged
            return currentCostRules;
        });
    };

    const addCostRuleLine = () => {
        props.setCostRules(currentCostRules => {
            const ruleIndex = currentCostRules.findIndex(rule => rule.priority === props.costRule.priority);
            if (ruleIndex > -1) {
                let updatedCostRules = [...currentCostRules];
                // Clone the CostRulelines array from the found rule
                let updatedCostRuleLines = [...updatedCostRules[ruleIndex].conditions];
                updatedCostRuleLines.push({
                    key: -1,
                    nominator: "equalto",
                    value: -1
                });
                
                // Update the rule with the new conditions
                updatedCostRules[ruleIndex] = { ...updatedCostRules[ruleIndex], conditions: updatedCostRuleLines };
    
                return updatedCostRules;
            }
        });
    };

    const removeCostRuleLine = (lineIndex) => {
        props.setCostRules(currentCostRules => {
            // Find the index of the rule that needs to be updated
            const ruleIndex = currentCostRules.findIndex(rule => rule.priority === props.costRule.priority);
    
            // If the rule is found, proceed to update
            if (ruleIndex > -1) {
                // Clone the stream rules array
                let updatedCostRules = [...currentCostRules];
                // Clone the CostRulelines array from the found rule
                let updatedCostRuleLines = [...updatedCostRules[ruleIndex].conditions];
                updatedCostRuleLines.splice(lineIndex,1);

                // Update the rule with the new conditions
                updatedCostRules[ruleIndex] = { ...updatedCostRules[ruleIndex], conditions: updatedCostRuleLines };
    
                return updatedCostRules;
            }
    
            return currentCostRules;
        });
    }

    const selectRuleLineKey = (selectedKey, index) => {
        const ruleLine = { ...props.costRule.conditions[index] }; // Create a copy of the ruleLine
        ruleLine.key = conditionValueSelect[selectedKey].key;

        // Get nominator options, value type, and listValues for the new key
        const conditionFollowUpOptions = getConditionFollowUpOptions(ruleLine.key);

        // Set nominator options for dropdown
        ruleLine.nominatorOptions = conditionFollowUpOptions.nominatorOptions;

        // Set value type for input element key
        // Possible values:
        // string, number, date, time, country_list, day_with_additional_list, relation_list, warehouse_list, warehouse_location_list, list
        ruleLine.valueType = conditionFollowUpOptions.valueType;

        // Set listValues for list type productfields
        ruleLine.listValues = conditionFollowUpOptions.listValues;

        ruleLine.nominator = ruleLine.nominatorOptions[0];

        if (conditionValueSelect[selectedKey]?.objectType === condition_object_type.ORDERFIELD) {
            ruleLine.objectType = condition_object_type.ORDERFIELD;
        } else if (conditionValueSelect[selectedKey]?.objectType === condition_object_type.PRODUCTFIELD) {
            ruleLine.objectType = condition_object_type.PRODUCTFIELD;
        } else {
            delete ruleLine.objectType;
        }

        if (textValueTypes.includes(ruleLine.valueType)) {
            ruleLine.value = "";
        } else if (booleanValueTypes.includes(ruleLine.valueType)) {
            ruleLine.value = false;
        }else if (numberValueTypes.includes(ruleLine.valueType)) {
            ruleLine.value = 0;
        } else if (dropdownValueTypes.includes(ruleLine.valueType)) {
            if (nominatorListValues.includes(ruleLine.nominator)) {
                ruleLine.value = [];
            } else {
                ruleLine.value = -1;
            }
        } else if (searchableListValueTypes.includes(ruleLine.valueType)) {
            ruleLine.value = [];
            ruleLine.fullOptionList = [];
        }

        // Update the stream rule in the parent component's state
        updateCostRuleLine(index, ruleLine);
    };

    const selectRuleLineCondition = (selectedNominator, index) => {
        const ruleLine = { ...props.costRule.conditions[index] }; // Create a copy of the ruleLine.
        const oldNominator = ruleLine.nominator;
        const newNominator = ruleLine.nominatorOptions[selectedNominator];
        ruleLine.nominator = newNominator;

        // Reset value when switching between single-select and multi-select nominators
        if (dropdownValueTypes.includes(ruleLine.valueType)) {
            const wasMultiSelect = nominatorListValues.includes(oldNominator);
            const isMultiSelect = nominatorListValues.includes(newNominator);
            if (wasMultiSelect !== isMultiSelect) {
                ruleLine.value = isMultiSelect ? [] : -1;
            }
        }

        updateCostRuleLine(index, ruleLine);
    };

    const selectRuleLineValue = (selectedValue, index) => {
        const ruleLine = { ...props.costRule.conditions[index] }; // Create a copy of the ruleLine.
        ruleLine.value = selectedValue;

        updateCostRuleLine(index, ruleLine);
    };

    const selectSearchableListValue = (selectedValue, index,option) => {
        const ruleLine = { ...props.costRule.conditions[index] }; // Create a copy of the ruleLine.
        ruleLine.value = selectedValue;
        
        const optionIndex = ruleLine.fullOptionList.findIndex(
            fo => fo.id === option.id
        );

        if (optionIndex === -1) {
            ruleLine.fullOptionList = [...ruleLine.fullOptionList, option];
        } else {
            ruleLine.fullOptionList = ruleLine.fullOptionList.filter(
                fo => fo.id !== option.id
            );
        }

        updateCostRuleLine(index, ruleLine);
    };

    const removeCostRule = () => {
        props.setCostRules(currentCostRules => {
            // Find the index of the rule that needs to be updated
            const ruleIndex = currentCostRules.findIndex(rule => rule.priority === props.costRule.priority);
            if(ruleIndex > -1) {
                // Clone the stream rules array
                let updatedCostRules = [...currentCostRules];
                updatedCostRules.splice(ruleIndex,1);

                return updatedCostRules;
            }
        });

    }

    const selectInvoiceProduct = (selected) => {
        props.setCostRules(currentCostRules => {
            // Find the index of the rule that needs to be updated
            const ruleIndex = currentCostRules.findIndex(rule => rule.priority === props.costRule.priority);
    
            // If the rule is found, proceed to update
            if (ruleIndex > -1) {
                // // Clone the stream rules array
                let updatedCostRules = [...currentCostRules];
                
                // updatedCostRules[ruleIndex].shipperId = selected;

                updatedCostRules[ruleIndex] = { 
                    ...updatedCostRules[ruleIndex],
                    invoiceProductId: selected,
                };
    
                return updatedCostRules;
            }
    
            return currentCostRules;
        });
    }

    const selectSecondaryInvoiceProductId = (selected) => {
        props.setCostRules(currentCostRules => {
            // Find the index of the rule that needs to be updated
            const ruleIndex = currentCostRules.findIndex(rule => rule.priority === props.costRule.priority);
    
            // If the rule is found, proceed to update
            if (ruleIndex > -1) {
                // // Clone the stream rules array
                let updatedCostRules = [...currentCostRules];
                
                // updatedCostRules[ruleIndex].shipperId = selected;

                updatedCostRules[ruleIndex] = { 
                    ...updatedCostRules[ruleIndex],
                    secondInvoiceProductId: selected,
                };
    
                return updatedCostRules;
            }
    
            return currentCostRules;
        });
    }
    const [open, setOpen] = useState(false);

    const confirmDeleteModal = {
        open: open,
        setOpen: setOpen,
        confirm: removeCostRule,
    };

    const handleDeleteClick = async () => {
        setOpen(true);
    };

    return (
        
        <Box
            style={style}
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            className={cx(``, {
            //   'is-dragging' : isDragging
            })}
            sx={{
                // background: "#FFF8F2",
                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                borderRadius:3,
                padding:2,
                width:500,
                overflow:"hidden",

                "& .postnl" : {
                    width:24,
                    height:24,
                },

                "& .dpd" : {
                    width:24,
                    height:24,
                },
                "& .dhl" : {
                    width:24,
                    height:24,
                },
                "& .bol" : {
                    width:32,
                    // width:24,
                    height:16,
                },

                "&.is-dragging": {
                    opacity:0,
                    // transform: 'rotate(-5deg)'
                },
                "@media screen and (max-width: 47.9375em)" : {
                    display:"flex",
                    flexDirection:"column",
                    // justifyContent:"flex-end",
                    alignItems:"flex-end"
                }
            }}
        >
            {/* Header */}
            {/* Colli */}
            <Box
                sx={{
                    display:"flex",
                    justifyContent:"space-between",
                    alignItems:props.invoiceFollowUpShipment ? "flex-start" : "center"
                }}
            >
                <Box
                    sx={{
                        flex:1,
                        display:'flex',
                        flexDirection:"column",
                    }}
                >
                    <DropdownBase
                        key={props.costRule.invoiceProductId}
                        lazyMount
                        displayName={"name"}
                        onChange={(selected) => selectInvoiceProduct(selected)}
                        noSelection={""} //Custom text when nothing is selected
                        options={props.invoiceProducts ?? []} // array
                        selected={props.invoiceProducts?.find(invoiceProduct => invoiceProduct.id === props.costRule.invoiceProductId)?.id ?? -1}
                        closeOnSelection
                        valueKey={"id"}
                        disallowDeselection

                        searchOptions={{
                            enabled:true,
                            placeholder:t("search")
                        }}
                    >
                        <Box 
                            sx={{
                                display:"inline-flex",
                                alignItems:"center",
                                gap:1,
                                height:32,
                                ml:"-8px",
                                pl:"8px",
                                pr:"8px",
                                borderRadius:4,
                                "&:hover": {
                                    // background:"#FFE9D6",
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                    cursor:"pointer",
                                },
                                // "@media screen and (max-width: 47.9375em)" : {
                                //     display:"inline",
                                //     width:"unset",
                                //     // width:1,
                                //     // display:"flex",
                                //     // justifyContent:"flex-end",
                                //     ml:0,
                                //     mr:"-8px"
                                // }
                            }}
                        >
                            <Text bold>{props.invoiceProducts.find(invoiceProduct => invoiceProduct.id === props.costRule.invoiceProductId)?.name}</Text>
                        </Box>
                    </DropdownBase>
                    {props.invoiceFollowUpShipment &&
                        <DropdownBaseV2
                            key={props.costRule.secondInvoiceProductId}
                            lazyMount
                            displayName={"name"}
                            onChange={(selected) => selectSecondaryInvoiceProductId(selected)}
                            noSelection={""} //Custom text when nothing is selected
                            options={props.invoiceProducts ?? []} // array
                            selected={props.invoiceProducts?.find(invoiceProduct => invoiceProduct.id === props.costRule.secondInvoiceProductId)?.id ?? -1}
                            closeOnSelection
                            valueKey={"id"}
                            disallowDeselection

                            searchOptions={{
                                enabled:true,
                                placeholder:t("search")
                            }}
                        >
                            {({anchorEl,onClick}) => (
                                <ButtonBase onClick={onClick}
                                    sx={{
                                        mt:0.5,
                                        justifyContent:'flex-start',
                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                        height:32,
                                        borderRadius:4,
                                        pl:0.5,
                                        pr:1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height:24,
                                            borderRadius:3,
                                            background:theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[100],
                                            px:1,
                                            lineHeight:'24px',
                                        }}
                                    >
                                        <Text>{t("follow_up_shipment")}</Text>
                                    </Box>
                                    <Box
                                        sx={{
                                            pl:1.5,
                                        }}
                                    >
                                        <Text bold>{props.invoiceProducts.find(invoiceProduct => invoiceProduct.id === props.costRule.secondInvoiceProductId)?.name ?? t("no_selection")}</Text>
                                    </Box>
                                </ButtonBase>
                            )}
                        </DropdownBaseV2>
                    }
                </Box>
            
                <Box
                    sx={{
                        display:'flex',
                        alignItems:'center',
                    }}
                >
                    {(props.isMobileWidth || props.likelyHeavy) && (
                        <Box
                            sx={{
                                mr:0.5,
                            }}
                        >
                            {props.index > 0 &&
                                <IconButton
                                    onClick={() => moveCostRule(props.index, props.index - 1)}
                                    disabled={props.index === 0}
                                    sx={{
                                        width:32,
                                        height:32,
                                        color:colors.txt["primary"],
                                    }}
                                >
                                    <NorthOutlinedIcon />
                                </IconButton>
                            }
                            {!props.disableDown &&
                                <IconButton
                                    onClick={() => moveCostRule(props.index, props.index + 1)}
                                    disabled={props.disableDown}
                                    sx={{
                                        width:32,
                                        height:32,
                                        color:colors.txt["primary"],
                                    }}
                                >
                                    <SouthOutlinedIcon />
                                </IconButton>
                            }
                        </Box>
                    )}
                    <ButtonBase
                        onClick={() => saveRule(props.costRule)}
                        sx={{
                            background:theme.palette.mode === "dark" ? colors.txt['secondary'] : colors.grey[400],
                            color:theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light'],
                            width:32,
                            height:32,
                            borderRadius:4,
                            mr:1,
                        }}
                    >
                        <SaveIcon fontSize='small'/>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => {
                            handleDeleteClick();
                            // removeCostRule(props.costRule)
                        }}
                        sx={{
                            color:colors.txt["secondary"],
                            width:32,
                            height:32,
                            borderRadius:4
                        }}
                    >
                        <DeleteOutlineOutlinedIcon/>
                    </ButtonBase>
                </Box>
            </Box>

            <Box
                sx={{
                    mt:2,
                    display:"flex",
                    flexDirection:"column",
                    gap:1,
                    width:1
                }}
            >
                {props.costRule.conditions && props.costRule.conditions.map((ruleline,index) => (
                    <ConditionRow
                        key={index}
                    >
                        {/* Colli */}
                        <DropdownBase
                            displayName={"name"} 
                            onChange={(selected) => selectRuleLineKey(selected,index)}
                            noSelection={""} 
                            lazyMount
                            options={conditionValueSelect}
                            useTranslation
                            selected={conditionValueSelect.findIndex(value => value.key === ruleline.key)}
                            closeOnSelection
                            disallowDeselection
                            searchOptions={{
                                enabled:true,
                                placeholder:t("search")
                            }}
                            // shComponent={<CustomComponent></CustomComponent>} // new
                        >
                            <KeyTrigger>
                                <Text noBreak>{conditionValueSelect?.find(value => value.key === ruleline.key) ? t(conditionValueSelect?.find(value => value.key === ruleline.key)?.name) : t("choose_condition")}</Text>
                            </KeyTrigger>
                        </DropdownBase>

                        {/* Nominator */}
                        <DropdownBase
                            displayName={"name"} 
                            onChange={(selected) => selectRuleLineCondition(selected,index)}
                            noSelection={""} //Custom text when nothing is selected
                            lazyMount
                            options={ruleline.nominatorOptions ?? []} // array
                            useTranslation // conditions uses translation keys as the array,
                            selected={ruleline && conditionValues?.conditions?.find(value => value.key === ruleline.key)?.nominators?.findIndex(value => value === ruleline.nominator) ?
                                conditionValues?.conditions?.find(value => value.key === ruleline.key)?.nominators?.findIndex(value => value === ruleline.nominator) : -1
                            }
                            closeOnSelection
                            disallowDeselection
                        >
                            <NominatorTrigger>
                                {windowSize > 768?
                                    typeof ruleline.nominator === 'Number' || ruleline.nominator === -1 ?
                                        <Text noBreak >{t('choose_condition')}</Text>
                                    :
                                        <Text noBreak bold>{t(ruleline.nominator)}</Text>
                                :
                                    <Text bold>{getMobileCondition(ruleline.nominator)}</Text>
                                }
                            </NominatorTrigger>
                        </DropdownBase>

                        {/* Value */}
                        
                        {textValueTypes.includes(ruleline.valueType) || numberValueTypes.includes(ruleline.valueType) ?
                            <ValueInputBox
                                className={cx('',{
                                    'error': props.errorMap && props.errorMap[index] && props.errorMap[index].value,
                                })}
                            >
                                <TextInput
                                    type={textValueTypes.includes(ruleline.valueType) ? "text" : "number"}
                                    value={ruleline.value}
                                    background={"transparent"}
                                    onChange={(e) => selectRuleLineValue(e.target.value,index)}
                                    // onChange={(e) => handleChange("firstname", e.target.value)}
                                />
                            </ValueInputBox>
                        :
                        booleanValueTypes.includes(ruleline.valueType) ? (
                            <ValueSwitchBox>
                                <ShSwitch
                                    color={theme.palette.mode === "dark" ? "primaryGrey" :"grey"}
                                    switchColor={theme.palette.mode === "dark" ? "#31415f" : "#31415f"}
                                    checked={ruleline.value}
                                    onChange={(event) => selectRuleLineValue(event.target.checked,index)}
                                />
                            </ValueSwitchBox>
                        ) :
                        searchableListValueTypes.includes(ruleline.valueType) ? (
                            <ShDropdownSearchQuery
                                name={ruleline.valueType}

                                displayName={"combined"}
                                onChange={(selected,option) => {
                                    selectSearchableListValue(selected,index,option);
                                    // handleChange("newWarehouseLocation",selected > -1 ?option : "");
                                    // setSelectedWarehouseLocationId(selected);
                                }}
                                selected={ruleline.value ?? [-1]}
                                multiple={true}
                                searchOptions={{
                                    placeholder:t("search")
                                }}
                                content={useGetProductsSelectQuery}
                            >

                                {({anchorEl,onClick}) => (
                                    <SearchTrigger
                                        onClick={onClick}
                                    >
                                        <Text noBreak>
                                            {ruleline.fullOptionList.length > 0 ? ruleline.fullOptionList.length > 1 ?
                                            ruleline.fullOptionList.length + " " + t("selected") : ruleline.fullOptionList[0]?.combined : t("no_selection")}
                                            {/* {getListOptions(ruleline?.valueType, props.countries, t)?.find(value => value.id === ruleline.value)?.name} */}
                                        </Text>
                                    </SearchTrigger>
                                )}
                            </ShDropdownSearchQuery>
                        ) :
                            nominatorListValues.includes(ruleline.nominator) ? (
                                <CRValueMultiSelect
                                    ruleline={ruleline}
                                    index={index}
                                    selectRuleLineValue={selectRuleLineValue}
                                    errorMap={props.errorMap}
                                    colors={colors}
                                    t={t}
                                    conditionValueSelect={conditionValueSelect}
                                    getObjects={getObjects}
                                    getListOptions={getListOptions}
                                    lazyMount
                                />
                            ) : (
                                <CRValueSingleSelect
                                    ruleline={ruleline}
                                    index={index}
                                    selectRuleLineValue={selectRuleLineValue}
                                    errorMap={props.errorMap}
                                    colors={colors}
                                    t={t}
                                    getListOptions={getListOptions}
                                    props={props}
                                    lazyMount
                                />
                            )
                        }

                        <RemoveButton
                            onClick={() => removeCostRuleLine(index)}
                        >
                            <CloseIcon fontSize='small'/>
                        </RemoveButton>

                    </ConditionRow>
                ))}

                <Box
                    onClick={addCostRuleLine}
                    sx={{
                        mt:1,
                        height:40,
                        display:"flex",
                        justifyContent:"center",
                        alignItems:"center",
                        borderRadius:5,
                        cursor:"pointer",
                        "&:hover" : {
                            background: colorSettings.costRule,
                        }
                    }}
                >
                    <Text semibold>Nieuwe voorwaarde</Text>
                </Box>
                
                
            </Box>

            <MConfirmDelete
                open={confirmDeleteModal.open}
                handleClose={() => confirmDeleteModal.setOpen(false)}
                // data={props.streamRule}
                confirm={confirmDeleteModal.confirm}
            />
        </Box>
    );
}

// Memoized so scroll-driven re-renders of the virtualized parent don't re-render
// every visible row (each row builds many dropdown subtrees). Rows only re-render
// when their own props change.
export default memo(CostRule);